import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '../../utils';

const state = 'mnzwitmspsjnunigpybgtuzj';
const HYDRA_CLIENT_ID: string = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID?.trim() || '';
const HYDRA_REDIRECT_URL: string = process.env.NEXT_PUBLIC_AUTH_REDIRECT_URL || '';

export interface AuthToken {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
  token_type: string;
}

type LoginChallengePayload = {
  codeChallenge: string;
};

export interface RedirectURLArgs {
  loginChallenge: string;
  googleAuthCode?: string;
  provider?: 'google' | 'password';
}

// Build Hydra authorization URL for full-page navigation (sets CSRF cookies correctly).
export const buildAuthorizationUrl = (codeChallenge: string, state: string): string => {
  const url = new URL('/oauth2/auth', window.location.origin);
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', HYDRA_CLIENT_ID);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('redirect_uri', HYDRA_REDIRECT_URL);
  url.searchParams.set('scope', 'offline_access openid');
  url.searchParams.set('state', state);
  return url.toString();
};

export const getLoginChallenge = async ({
  codeChallenge,
}: LoginChallengePayload): Promise<string | undefined> => {
  try {
    const params = new URLSearchParams({
      client_id: HYDRA_CLIENT_ID,
      redirect_uri: HYDRA_REDIRECT_URL,
      code_challenge: codeChallenge,
      state,
      scope: 'offline_access openid',
    });
    const res = await fetch(`/api/auth/login-challenge?${params.toString()}`, {
      credentials: 'include',
    });
    if (!res.ok) {
      console.error('Backend login-challenge endpoint failed', res.status);
      return;
    }
    const data = (await res.json()) as { login_challenge?: string };
    if (!data?.login_challenge) {
      console.error('Failed to extract login URL');
      return;
    }
    return data.login_challenge;
  } catch (err) {
    console.error('Error while fetching login challenge', err);
    return;
  }
};

export const getRedirectURL = async (
  loginChallenge: string,
  googleAuthCode?: string,
  affiliateId?: string,
  provider?: 'google' | 'password'
): Promise<string | undefined> => {
  const payload: any = {
    loginChallenge,
    affiliateId,
  };
  if (googleAuthCode) payload.googleAuthCode = googleAuthCode;
  if (provider) payload.provider = provider;

  const response = await axiosInstance.post(`/api/auth/login`, payload, { withCredentials: true });

  const { redirectURL } = response.data;

  if (!redirectURL) {
    // Log an error or handle it accordingly
    console.error('Redirect URL not found in response data');
    return;
  }

  return redirectURL;
};

export const getConsentChallenge = async (redirectURL: string): Promise<string> => {
  try {
    const res = await fetch(redirectURL, { redirect: 'manual', credentials: 'include' });
    const locationHeader = res.headers.get('Location') ?? res.headers.get('location');
    const targetUrl: string = locationHeader ?? res.url;
    if (!targetUrl) {
      console.error('Hydra login accept did not return a consent redirect');
      return '';
    }
    const absolute = new URL(targetUrl, window.location.origin);
    const consentChallenge = absolute.searchParams.get('consent_challenge') || '';
    if (!consentChallenge) {
      console.error('Failed to extract consent challenge');
      return '';
    }
    return consentChallenge;
  } catch (err) {
    console.error('Error while fetching consent challenge', err);
    return '';
  }
};

export const getCodeFromConcent = async (consentChallenge: string): Promise<string> => {
  try {
    const consentResp = await axiosInstance.post('/api/auth/consent', {
      consentChallenge: consentChallenge,
    });

    const redirectCURL: string | undefined = consentResp?.data?.redirectURL;
    if (!redirectCURL) {
      console.error('Backend did not return consent redirectURL');
      return '';
    }

    const consentRes = await fetch(redirectCURL, { redirect: 'manual', credentials: 'include' });
    const locationHeader = consentRes.headers.get('Location') ?? consentRes.headers.get('location');
    const targetUrl: string = locationHeader ?? consentRes.url;
    if (!targetUrl) {
      console.error('Hydra consent accept did not return a code redirect');
      return '';
    }
    const absolute = new URL(targetUrl, window.location.origin);
    const code = absolute.searchParams.get('code') || '';
    if (!code) {
      console.error('Redirect URL not found in response data');
      return '';
    }
    return code;
  } catch (err) {
    console.error('Error while fetching auth code from consent', err);
    return '';
  }
};

export const getAuthToken = async ({
  code,
  codeVerifier,
}: {
  code: string;
  codeVerifier: string;
}): Promise<AuthToken | undefined> => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/x-www-form-urlencoded');

  const urlencoded = new URLSearchParams();
  urlencoded.append('client_id', String(HYDRA_CLIENT_ID));
  urlencoded.append('code', String(code));
  urlencoded.append('grant_type', 'authorization_code');
  urlencoded.append('redirect_uri', String(HYDRA_REDIRECT_URL));
  urlencoded.append('code_verifier', String(codeVerifier));

  const requestOptions = {
    method: 'POST',
    headers,
    body: urlencoded,
  };

  const response = await fetch('/oauth2/token', requestOptions);
  const tokenResponse = await response?.json();
  if (!response.ok) {
    console.error('Token exchange failed', tokenResponse);
  }
  return tokenResponse;
};

export const getLogoutChallenge = async () => {
  try {
    const fetchUrl = `/oauth2/sessions/logout`;
    const res = await fetch(fetchUrl, { redirect: 'manual', credentials: 'include' });
    const locationHeader = res.headers.get('Location') ?? res.headers.get('location');
    const targetUrl: string = locationHeader ?? res.url;
    if (!targetUrl) {
      console.error('Hydra logout did not return a challenge redirect');
      return '';
    }
    const absolute = new URL(targetUrl, window.location.origin);
    const logoutChallenge = absolute.searchParams.get('logout_challenge') || '';
    if (!logoutChallenge) {
      console.error('Failed to extract logout challenge');
      return '';
    }
    return logoutChallenge;
  } catch (err) {
    console.error('Error while fetching logout challenge', err);
    return '';
  }
};

export const handleLogout = async (logoutChallenge: string) => {
  const resp = await axiosInstance.post('/api/auth/logout', {
    logoutChallenge: logoutChallenge,
  });
  const redirectURL: string | undefined = resp?.data?.redirectURL;
  const logoutVerifier = redirectURL
    ? redirectURL.split('logout_verifier=')[1]?.substring(0, 36)
    : undefined;
  await fetch(`/oauth2/sessions/logout?logout_verifier=${logoutVerifier}`);
};

export const getRefreshAuthToken = async ({
  refreshToken,
  codeVerifier,
}: {
  refreshToken: string;
  codeVerifier: string;
}): Promise<AuthToken | undefined> => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/x-www-form-urlencoded');

  const urlencoded = new URLSearchParams();
  urlencoded.append('client_id', String(HYDRA_CLIENT_ID));
  urlencoded.append('grant_type', 'refresh_token');
  urlencoded.append('refresh_token', String(refreshToken));
  // Do NOT send code_verifier for refresh_token grant. PKCE is only for the authorization_code grant.

  const requestOptions = {
    method: 'POST',
    headers,
    body: urlencoded,
  };

  const response = await fetch('/oauth2/token', requestOptions);
  const tokenResponse = await response?.json();
  if (!response.ok) {
    console.error('Refresh token failed', tokenResponse);
  }
  return tokenResponse;
};

export const adminLoginAsUser = async (
  adminId: string,
  targetUserId: string,
  loginChallenge: string
): Promise<string | undefined> => {
  const payload = {
    adminId,
    targetUserId,
    loginChallenge,
  };

  const response = await axiosInstance.post(`/api/auth/admin-login-as-user`, payload);

  const { redirectURL } = response.data;

  if (!redirectURL) {
    console.error('Redirect URL not found in response data');
    return;
  }

  return redirectURL;
};


const getProviderRedirectURL = async (
  provider: string,
): Promise<string | undefined> => {
  const response = await axiosInstance.get(`/api/social-media/connect/${provider}?platform=${provider}`);

  const { redirectURL } = response.data;

  if (!redirectURL) {
    // Log an error or handle it accordingly
    console.error('Redirect URL not found in response data');
    return;
  }

  return redirectURL;
};

export const usegetProviderRedirectURL = () => {
  return useMutation(getProviderRedirectURL, { onSuccess: (data) => data });
};